import React, { useState, useEffect, useCallback } from "react";
import ToolTip from "components/tooltip";
import CustomTable from "components/custom_table/";
import { getUserActivityTable } from "../dataProvider/data-provider";
import { DefaultSpinner, NoData } from "components";
import LocationScopeGate from "hooks/useAdminGroup/LocationScopeGate";
import { useScopedApiFilters } from "hooks/useAdminGroup/useScopedApiFilters";

const navControl = ["Recent", "Problematic"];

const urls = {
  recent: `/nextos/reports/quality/call-log/recent`,
  problematic: `/nextos/reports/quality/call-log/problematic`
};

const QualityUserTable = ({ apiFilters, timeRange }) => {
  const [state, setState] = useState({
    activeNavPills: "recent",
    isLoadingTableData: false,
    table: {}
  });

  const handleNavPills = menu => {
    setState(prevState => ({
      ...prevState,
      activeNavPills: menu.toLocaleLowerCase()
    }));
    getTableData(menu.toLocaleLowerCase());
  };

  const getTableData = useCallback(
    async activeTab => {
      // Mark loading for EVERY trigger (mount, pill switch, scope or time
      // range change), not just pill switches, so refetches never leave the
      // previous rows on screen with no indicator.
      setState(prevState => ({
        ...prevState,
        isLoadingTableData: true
      }));
      const tableData = await getUserActivityTable({
        url: urls[activeTab],
        filters: apiFilters,
        timeRange
      });
      // A newer call cancelled this one (getUserActivityTable uses the
      // cancel-previous axios client); its own continuation owns the state.
      if (tableData && tableData.isCancel) {
        return;
      }
      setState(prevState => ({
        ...prevState,
        activeNavPills: activeTab,
        table: tableData.table,
        isLoadingTableData: false
      }));
    },
    [timeRange, apiFilters]
  );

  useEffect(() => {
    getTableData(state.activeNavPills);
  }, [apiFilters, timeRange]);
  useEffect(() => {
    getTableData(state.activeNavPills);
  }, []);

  return (
    <div className="connected-calls-container">
      <div className="connected-calls-header">
        <div className="title-content">
          <h4>Connected Calls</h4>
        </div>
      </div>
      <div className="log-list">
        <div className="nav-control">
          <ul>
            {navControl.map((menu, index) => (
              <li
                key={index}
                className={
                  state.activeNavPills === menu.toLocaleLowerCase()
                    ? "active"
                    : ""
                }
                onClick={() => handleNavPills(menu)}
              >
                {menu}{" "}
                {menu === "Recent" && (
                  <ToolTip tipContent="Shows the last hour of calls with quality ratings." />
                )}
              </li>
            ))}
          </ul>
        </div>
        <CustomTable 
          table={state.table}
          isLoading={state.isLoadingTableData}
        />
      </div>
    </div>
  );
};

// The Connected Calls table fetches /quality/call-log/{recent|problematic}
// directly (never through ReportPageChart), so the breadcrumb scope is
// attached here. The Location view seeds apiFilters.locations from the
// /nextos/locations options, so this is the one quality fetcher where the
// degraded-seed recovery in deriveScopedApiFilters genuinely matters -- the
// same Mongo seed the charts recover from in ReportPageChartWithScope.
// The gate blocks mounting until the scope resolves, which also blocks the
// table's fetch effect; scope changes flow through the memoized apiFilters
// identity into the existing useEffect dependency.
const QualityUserTableWithScope = (props) => {
  const { scope, apiFilters, hold, isEmptyIntersection } = useScopedApiFilters(
    props.apiFilters,
    props.filterOptions,
  );

  return (
    <LocationScopeGate scope={scope}>
      {hold ? (
        <DefaultSpinner />
      ) : isEmptyIntersection ? (
        <NoData />
      ) : (
        <QualityUserTable {...props} apiFilters={apiFilters} />
      )}
    </LocationScopeGate>
  );
};

export default QualityUserTableWithScope;
